import { useEffect, useRef } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { CircleCheck } from 'lucide-react'
import { AuthLayout } from '@/components/AuthLayout'
import { useAuth } from '@/lib/AuthContext'
import { supabase } from '@/lib/supabaseClient'
import { Telemetry } from '@/lib/telemetry'

/**
 * Landing page for Supabase's email-verification redirect.
 *
 * Two flows land here:
 *
 * 1. Magic-link OTP for signup (what the commons public signup uses).
 *    Supabase's redirect after OTP click brings the user here WITH a
 *    session already established from the URL hash fragment. We
 *    auto-navigate to `/` — `ProtectedRoute` + `AuthContext` handle
 *    the real routing from there: active commons members → portfolio
 *    list; denied users → `/auth/login` with the flash written by
 *    `denyCommonsSession`. Showing the "please sign in" UI in this
 *    case is actively misleading — the user IS signed in, and
 *    clicking "Sign in" on a page where you're already signed in is
 *    confusing UX.
 *
 * 2. Plain email-confirmation redirect (Supabase "Confirm signup"
 *    template, if ever configured — commons doesn't use this today).
 *    No session is established by the click. Fall through to the
 *    "please sign in" UI so the user can proceed manually.
 *
 * A2F-633 architectural: before the deny-doesn't-signout refactor,
 * auto-navigating to `/` from here would have been pointless — the
 * OTP session was being destroyed by AuthContext's init effect before
 * anything downstream could use it. Now the session is preserved, so
 * ProtectedRoute sees the real authoritative state and routes
 * accordingly.
 */
export function VerifiedPage() {
  const navigate = useNavigate()
  const { user, loading } = useAuth()
  const emailVerifiedFired = useRef(false)
  const userSignedInFired = useRef(false)

  // Fire email_verified once on mount — covers all three landing paths
  // (magic-link OTP success, denied session, plain confirm). See A2F-782.
  useEffect(() => {
    if (emailVerifiedFired.current) return
    emailVerifiedFired.current = true
    Telemetry.emailVerified()
  }, [])

  // Wait for AuthContext to settle before navigating — if we race ahead,
  // SigninPage might mount and read sessionStorage BEFORE the deny flash
  // is written, leaving the denied user on /auth/login with no explanation.
  //
  // Load-bearing invariant: `loading=false` ⇒ AuthContext has already
  // run its init effect, including fetchMe and (for denied users) the
  // `denyCommonsSession` flash write. This holds because supabase-js's
  // `getSession()` awaits `_initialize()` (which consumes the OTP URL
  // hash), so init's getSession returns the OTP session before init's
  // `finally` flips `loading` to `false`. Changing AuthContext to flip
  // `loading=false` before `fetchMe` resolves would silently re-break
  // this page — flash would land in sessionStorage after SigninPage's
  // mount effect already read+cleared it.
  useEffect(() => {
    if (loading) return
    let cancelled = false
    async function routeByAuthState() {
      if (user) {
        // Fully authenticated commons member — fire the magic-link sign-in
        // event (path 1) before navigating. SigninPage fires `email`; this
        // path fires `magic_link`. See A2F-784.
        if (!userSignedInFired.current) {
          userSignedInFired.current = true
          Telemetry.userSignedIn({ auth_method: 'magic_link' })
        }
        if (!cancelled) navigate('/', { replace: true })
        return
      }
      // Auth settled but no user. Check if a Supabase session exists that
      // just didn't resolve to a commons member (denied user whose flash
      // was already written, or a silent fetchMe failure like a network
      // hiccup). In both cases send to the sign-in page: the deny flash
      // (if written) surfaces there; otherwise the user can retry.
      const { data } = await supabase.auth.getSession()
      if (cancelled) return
      if (data.session) {
        navigate('/auth/login', { replace: true })
      }
      // else: no session (plain email-confirm flow with no auto-login)
      // — fall through to the manual "Sign in" UI below.
    }
    routeByAuthState()
    return () => {
      cancelled = true
    }
  }, [loading, user, navigate])

  return (
    <AuthLayout title="Email verified">
      <div className="flex flex-col items-center text-center">
        <CircleCheck className="w-12 h-12 text-green-500 mb-4" />
        <p className="text-sm text-slate-600 leading-relaxed mb-6">
          Your email has been verified. You can now sign in to browse the
          CTDCommons archive.
        </p>
        <Link
          to="/auth/login"
          className="w-full px-4 py-2 bg-brand-orange text-white text-sm font-medium rounded-md hover:bg-brand-orange/90 transition-colors text-center"
        >
          Sign in
        </Link>
      </div>
    </AuthLayout>
  )
}
